import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ExternalLink, Layers, Package, TrendingUp, HelpCircle,
  BookOpen, Layout, FileImage, Plus, Globe,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@/contexts/PageContext';
import {
  usePartners, useProducts, usePackages, useCases, useFAQs,
  usePrograms, useFrameworks, useAssets, usePartnershipsAccess, useMarkets, canEditSection,
} from '@/hooks/usePartnerships';
import { ProgramFormDialog } from '@/components/partnerships/ProgramFormDialog';
import { FrameworkFormDialog } from '@/components/partnerships/FrameworkFormDialog';
import { AssetFormDialog } from '@/components/partnerships/AssetFormDialog';
import { MarketFormDialog } from '@/components/partnerships/MarketFormDialog';
import { PARTNER_CONFIG, PartnerThemeProvider } from '@/components/partnerships/PartnerTheme';
import type {
  PartnerSlug, Program, Framework, Asset, Market,
} from '../../../../shared/types/partnerships';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function PartnerHubPage() {
  const { t } = useTranslation();
  const { partner: slug } = useParams<{ partner: string }>();
  const navigate = useNavigate();

  const [editProgram, setEditProgram] = useState<Program | undefined>();
  const [editFramework, setEditFramework] = useState<Framework | undefined>();
  const [editAsset, setEditAsset] = useState<Asset | undefined>();
  const [editMarket, setEditMarket] = useState<Market | undefined>();
  const [createProgram, setCreateProgram] = useState(false);
  const [createFramework, setCreateFramework] = useState(false);
  const [createAsset, setCreateAsset] = useState(false);
  const [createMarket, setCreateMarket] = useState(false);

  const { data: access } = usePartnershipsAccess();
  const { data: partners = [], isPending: partnersPending } = usePartners();
  const { setPartnerCtx } = usePageContext();

  const partnerSlug = slug as PartnerSlug;
  const config = PARTNER_CONFIG[partnerSlug];

  const partner = partners.find(p => p.slug === partnerSlug);
  const partnerId = partner?.partnerId || '';
  const canEdit = canEditSection(access?.editScopes, 'all');

  useEffect(() => {
    if (!partnerId || !config) return;
    setPartnerCtx({ partnerId, partnerName: config.name, partnerSlug });
    return () => setPartnerCtx(null);
  }, [partnerId, partnerSlug]);

  const { data: products = [] } = useProducts(partnerId);
  const { data: packages = [] } = usePackages(partnerId);
  const { data: cases = [] } = useCases(partnerId);
  const { data: faqs = [] } = useFAQs(partnerId);
  const { data: programs = [], isPending: pendingPrograms } = usePrograms(partnerId);
  const loadingPrograms = partnersPending || (!!partnerId && pendingPrograms);
  const { data: frameworks = [], isPending: pendingFrameworks } = useFrameworks(partnerId);
  const loadingFrameworks = partnersPending || (!!partnerId && pendingFrameworks);
  const { data: assets = [], isPending: pendingAssets } = useAssets(partnerId);
  const loadingAssets = partnersPending || (!!partnerId && pendingAssets);
  const { data: markets = [], isLoading: loadingMarkets } = useMarkets();

  // El "no encontrado" va DESPUÉS de todos los hooks, no antes. Con el return
  // temprano arriba, navegar de un partner válido a un slug inválido (sin
  // remontar) cambiaba la cantidad de hooks entre renders y React tiraba
  // "Rendered more hooks than during the previous render". Los hooks de datos
  // ya toleran partnerId vacío (`enabled: !!partnerId`), así que llamarlos sin
  // partner no dispara ninguna query.
  if (!config) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{t('partnerships.common.partner_not_found')}</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/partnerships')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('partnerships.common.back_to_hub')}
        </Button>
      </div>
    );
  }


  const quickNav = [
    {
      icon: Layers,
      label: t('partnerships.common.products'),
      count: products.length,
      href: `/partnerships/${partnerSlug}/products`,
      description: t('partnerships.hub.nav_products_desc'),
    },
    {
      icon: Package,
      label: t('partnerships.common.packages'),
      count: packages.length,
      href: `/partnerships/${partnerSlug}/packages`,
      description: t('partnerships.hub.nav_packages_desc'),
    },
    {
      icon: TrendingUp,
      label: t('partnerships.common.cases'),
      count: cases.length,
      href: `/partnerships/${partnerSlug}/cases`,
      description: t('partnerships.hub.nav_cases_desc'),
    },
    {
      icon: HelpCircle,
      label: 'FAQ',
      count: faqs.length,
      href: `/partnerships/${partnerSlug}/faq`,
      description: t('partnerships.hub.nav_faq_desc'),
    },
  ];

  return (
    <PartnerThemeProvider slug={partnerSlug}>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/partnerships" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: config.color }} />
              <h1 className="text-3xl font-bold tracking-tight">{config.name}</h1>
            </div>
            {partner?.description && (
              <p className="text-muted-foreground mt-1">{partner.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {partner?.driveFolder && (
              <a href={partner.driveFolder} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Drive
                </Button>
              </a>
            )}
            {!partner?.driveFolder && partner?.driveUrl && (
              <a href={partner.driveUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">{t('partnerships.hub.master_deck')}</Button>
              </a>
            )}
          </div>
        </div>

        {/* Quick nav grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickNav.map(({ icon: Icon, label, count, href, description }) => (
            <Link
              key={href}
              to={href}
              className="group border border-border rounded-lg p-5 bg-card hover:border-primary/40 hover:shadow-xs transition-all flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div
                  className="h-9 w-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${config.color}18` }}
                >
                  <Icon className="h-4.5 w-4.5" style={{ color: config.color }} />
                </div>
                {count > 0 && (
                  <span className="text-2xl font-bold tabular-nums">{count}</span>
                )}
              </div>
              <div>
                <p className="font-semibold text-sm group-hover:text-primary transition-colors">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Programs section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{t('partnerships.hub.programs_section')}</h2>
              {programs.length > 0 && <span className="text-xs text-muted-foreground">({programs.length})</span>}
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={() => setCreateProgram(true)}>
                <Plus className="h-3 w-3" /> {t('partnerships.common.new')}
              </Button>
            )}
          </div>
          {loadingPrograms ? (
            <div className="flex items-center justify-center py-8"><WinclapLoader /></div>
          ) : programs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t('partnerships.hub.programs_empty')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {programs.map(prog => (
                <div key={prog.programId} className="group relative border rounded-lg p-4 space-y-2 hover:shadow-md transition-shadow bg-card">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-sm leading-tight">{prog.name}</h3>
                    {canEdit && (
                      <button
                        onClick={() => setEditProgram(prog)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted shrink-0"
                      >
                        <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {prog.description && <p className="text-xs text-muted-foreground line-clamp-2">{prog.description}</p>}
                  {prog.objective && <p className="text-xs text-muted-foreground"><span className="font-medium">{t('partnerships.hub.program_objective_label')}</span> {prog.objective}</p>}
                  {prog.availableMarkets.length > 0 && (
                    <p className="text-xs text-muted-foreground">{prog.availableMarkets.join(' · ')}</p>
                  )}
                  {prog.driveUrl && (
                    <a href={prog.driveUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <ExternalLink className="h-3 w-3" /> {t('partnerships.common.view_in_drive')}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Frameworks section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layout className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Frameworks</h2>
              {frameworks.length > 0 && <span className="text-xs text-muted-foreground">({frameworks.length})</span>}
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={() => setCreateFramework(true)}>
                <Plus className="h-3 w-3" /> {t('partnerships.common.new')}
              </Button>
            )}
          </div>
          {loadingFrameworks ? (
            <div className="flex items-center justify-center py-8"><WinclapLoader /></div>
          ) : frameworks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t('partnerships.hub.frameworks_empty')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {frameworks.map(fw => (
                <div key={fw.frameworkId} className="group relative border rounded-lg p-4 space-y-2 hover:shadow-md transition-shadow bg-card">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-sm leading-tight">{fw.name}</h3>
                    {canEdit && (
                      <button
                        onClick={() => setEditFramework(fw)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted shrink-0"
                      >
                        <Layout className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {fw.description && <p className="text-xs text-muted-foreground line-clamp-3">{fw.description}</p>}
                  {fw.useCases && fw.useCases.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {fw.useCases.slice(0, 3).map((uc, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{uc}</span>
                      ))}
                    </div>
                  )}
                  {fw.driveUrl && (
                    <a href={fw.driveUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <ExternalLink className="h-3 w-3" /> {t('partnerships.common.view_in_drive')}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Assets section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileImage className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Assets</h2>
              {assets.length > 0 && <span className="text-xs text-muted-foreground">({assets.length})</span>}
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={() => setCreateAsset(true)}>
                <Plus className="h-3 w-3" /> {t('partnerships.common.new')}
              </Button>
            )}
          </div>
          {loadingAssets ? (
            <div className="flex items-center justify-center py-8"><WinclapLoader /></div>
          ) : assets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t('partnerships.hub.assets_empty')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {assets.map(a => (
                <div key={a.assetId} className="group relative border rounded-lg p-4 space-y-2 hover:shadow-md transition-shadow bg-card">
                  <div className="flex items-start justify-between gap-1">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{a.assetType}</p>
                      <h4 className="text-sm font-medium leading-tight mt-0.5">{a.name}</h4>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => setEditAsset(a)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted shrink-0"
                      >
                        <FileImage className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {a.description && <p className="text-xs text-muted-foreground line-clamp-2">{a.description}</p>}
                  <a href={a.driveUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="h-3 w-3" /> {t('partnerships.common.open_in_drive')}
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Markets section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{t('partnerships.hub.markets_section')}</h2>
              {markets.length > 0 && <span className="text-xs text-muted-foreground">({markets.length})</span>}
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={() => setCreateMarket(true)}>
                <Plus className="h-3 w-3" /> {t('partnerships.common.new')}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('partnerships.hub.markets_global_note')}
          </p>
          {loadingMarkets ? (
            <div className="flex items-center justify-center py-8"><WinclapLoader /></div>
          ) : markets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t('partnerships.hub.markets_empty')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {markets.map(m => (
                <div key={m.marketId} className="group relative border rounded-lg p-4 space-y-1 hover:shadow-md transition-shadow bg-card">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm">{m.name}</p>
                      <p className="text-xs text-muted-foreground">{m.countryCode}{m.region ? ` · ${m.region}` : ''}{m.currency ? ` · ${m.currency}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${m.active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                        {m.active ? t('partnerships.hub.market_active') : t('partnerships.hub.market_inactive')}
                      </span>
                      {canEdit && (
                        <button
                          onClick={() => setEditMarket(m)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                        >
                          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                  {m.notes && <p className="text-xs text-muted-foreground">{m.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Dialogs */}
        {partnerId && (
          <>
            <ProgramFormDialog
              open={createProgram || !!editProgram}
              onOpenChange={o => { if (!o) { setCreateProgram(false); setEditProgram(undefined); } }}
              partnerId={partnerId}
              program={editProgram}
            />
            <FrameworkFormDialog
              open={createFramework || !!editFramework}
              onOpenChange={o => { if (!o) { setCreateFramework(false); setEditFramework(undefined); } }}
              partnerId={partnerId}
              framework={editFramework}
            />
            <AssetFormDialog
              open={createAsset || !!editAsset}
              onOpenChange={o => { if (!o) { setCreateAsset(false); setEditAsset(undefined); } }}
              partnerId={partnerId}
              asset={editAsset}
            />
          </>
        )}
        <MarketFormDialog
          open={createMarket || !!editMarket}
          onOpenChange={o => { if (!o) { setCreateMarket(false); setEditMarket(undefined); } }}
          market={editMarket}
        />
      </div>
    </PartnerThemeProvider>
  );
}
